<template>
  <div>
    <SetupSteps v-if="!setupComplete" />
    <FormCard
      v-model="merchant"
      :create-button-text="submitButtonText"
      @formSubmit="$emit('formSubmit', $event)">
      <CardForm
        :resource="merchant"
        resource-name="company"
        :section-title="$t('common.merchantProfile')">
        <TextInput
          v-model="merchant.name"
          name="company[name]"
          :custom-label="$t('common.name')"
          required />
        <div class="uploader-group">
          <div class="uploader-group-item col-md-6 col-24">
            <ImageUpload
              v-model="companyLogo"
              name="company[logo]"
              :custom-label="$t('company.profilePicture')"
              :helper-text="$t('company.recommendedProfileSize')" />
          </div>
          <div class="uploader-group-item col-md-18 col-24">
            <ImageUpload
              v-model="companyCover"
              name="company[cover]"
              :custom-label="$t('common.coverPhoto')"
              :helper-text="$t('company.recommendedCoverSize')" />
          </div>
        </div>
        <div class="form-wysiwyg">
          <label class="custom-label-group">{{ $t('common.description') }}</label>
          <editor v-model="merchant.description"
                  :init="initValue"
                  name="company[description]" />
        </div>
      </CardForm>
      <CardForm
        :resource="merchant"
        resource-name="company"
        :section-title="$t('company.contactDetails')"
        :subtitle="$t('company.publicContactInfo')">
        <div class="row">
          <TextInput
            v-model="merchant.email"
            class="col-24 col-md-8"
            name="company[email]"
            :custom-label="$t('common.email')"
            required />
          <div class="form-group custom-form-group mb-0 col-24 col-md-8">
            <label for="mobileInput" class="custom-form-group-label">{{ $t('common.mobile') }}</label>
            <vue-tel-input
              ref="mobileInput"
              v-model="merchant.phone_number"
              input-id="mobileInput"
              name="company[phone_number]"
              :ignored-countries="['IL']"
              :enabled-country-code="true"
              :preferred-countries="['LB', 'SA']"
              :valid-characters-only="true"
              :default-country="defaultCountryCode"
              :disabled-fetching-country="true"
              mode="national"
              wrapper-classes="ihjoz-tel-input"
              input-classes="ihjoz-tel-input-field ihjoz-tel-input-field-code-visible"
              @onInput="onInput"
              @input="validateNumber" />
            <input
              :value="selectedPhoneCountryCode"
              name="company[phone_country_code]"
              type="hidden">
          </div>
          <TextInput
            v-model="merchant.link"
            class="col-24 col-md-8"
            name="company[link]"
            prepend="https://"
            :custom-label="$t('company.website')" />
          <TextInput
            v-model="merchant.facebook_link"
            class="col-24 col-md-8"
            name="company[facebook_link]"
            prepend="https://"
            custom-label="Facebook" />
          <TextInput
            v-model="merchant.twitter_link"
            class="col-24 col-md-8"
            name="company[twitter_link]"
            prepend="https://"
            custom-label="Twitter" />
          <TextInput
            v-model="merchant.instagram_link"
            class="col-24 col-md-8"
            name="company[instagram_link]"
            prepend="https://"
            custom-label="Instagram" />
          <TextInput
            v-model="merchant.linkedin_link"
            class="col-24 col-md-8"
            name="company[linkedin_link]"
            prepend="https://"
            custom-label="LinkedIn" />
        </div>
      </CardForm>
      <CardForm
        :resource="merchant"
        resource-name="company"
        :section-title="$t('common.address')">
        <MultilineTextInput
          v-model="merchant.address"
          name="company[address]"
          :custom-label="$t('common.address')"
          @blur="locateAddress()" />
        <div class="form-card-group">
          <div class="form-card-group-item col-24 col-md-9">
            <ItemSelect
              v-model="merchant.country_id"
              :items="countries"
              name="company[country_id]"
              :label="$t('common.country')"
              :disabled="setupComplete"
              :shortened="false"
              required
              @blur="locateAddress()" />
          </div>
          <div class="form-card-group-item col-24 col-md-9">
            <TextInput
              v-model="merchant.city"
              name="company[city]"
              :custom-label="$t('common.city')"
              @blur="locateAddress()" />
          </div>
          <div class="form-card-group-item col-24 col-md-6">
            <TextInput
              v-model="merchant.zip_code"
              name="company[zip_code]"
              :custom-label="$t('common.zipCode')"
              @blur="locateAddress()" />
          </div>
        </div>
        <HiddenInput
          v-model="merchant.latitude"
          name="company[latitude]" />
        <HiddenInput
          v-model="merchant.longitude"
          name="company[longitude]" />
        <div class="form-group custom-form-group">
          <label class="custom-form-group-label">
            {{ $t('common.map') }}
          </label>
          <div class="custom-form-group-radio custom-form-group-radio-small-column">
            <div class="custom-radio-button">
              <input id="company-map-setting-0"
                     v-model="merchant.map_setting"
                     class="custom-radio-button-input"
                     type="radio"
                     name="company[map_setting]"
                     value="0">
              <label for="company-map-setting-0" class="custom-radio-button-label">
                <span>{{ $t('common.locateAutomatically') }}</span>
              </label>
            </div>
            <div class="custom-radio-button">
              <input id="company-map-setting-1"
                     v-model="merchant.map_setting"
                     class="custom-radio-button-input"
                     type="radio"
                     name="company[map_setting]"
                     value="1">
              <label for="company-map-setting-1" class="custom-radio-button-label">
                <span>{{ $t('common.pinOnMap') }}</span>
              </label>
            </div>
            <div class="custom-radio-button">
              <input id="company-map-setting-2"
                     v-model="merchant.map_setting"
                     class="custom-radio-button-input"
                     type="radio"
                     name="company[map_setting]"
                     value="2">
              <label for="company-map-setting-2" class="custom-radio-button-label">
                <span> {{ $t('common.hide') }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="form-card-map custom-form-group" :class="{ 'mb-n4': !isMapVisible }">
          <div v-show="isMapVisible" id="google-map" style="height: 400px;" />
        </div>
      </CardForm>
    </FormCard>
  </div>
</template>

<script>
import SetupSteps from "../Setup/SetupSteps.vue";
import FormCard from "../../../shared/components/UI/FormCard.vue";
import CardForm from "../../../shared/components/UI/CardForm.vue";
import TextInput from "../../../shared/components/UI/TextInput.vue";
import MultilineTextInput from "../UI/MultilineTextInput.vue";
import ItemSelect from "../../../shared/components/UI/ItemSelect.vue";
import Editor from "@tinymce/tinymce-vue";
import ImageUpload from "../../../shared/components/UI/ImageUpload.vue";
import { VueTelInput } from "vue-tel-input";

import SetupStore from "../../../shared/components/Setup/SetupStore";

export default {
  name: "CompanyForm",

  components: {
    SetupSteps,
    FormCard,
    CardForm,
    TextInput,
    MultilineTextInput,
    ItemSelect,
    Editor,
    ImageUpload,
    VueTelInput
  },

  props: ["company", "countries"],

  data: function () {
    return {
      merchant: this.company,
      defaultCountryCode: Dashboard.defaultCountryInfo.alpha2,
      selectedPhoneCountryCode: Dashboard.defaultCountryInfo.dialCode,
      initValue: {
        theme: "silver",
        skin: false,
        language: this.$i18n.locale,
        branding: false,
        images_upload_url: "/api/v4/upload_image",
        paste_data_images: true,
        relative_urls: false,
        content_css: Dashboard.stylesheetPath,
        toolbar: "insertfile undo redo | styleselect fontselect fontsizeselect | bold italic underline strikethrough | ltr rtl alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link anchor image media | preview code fullscreen | forecolor backcolor removeformat| pagebreak | charmap table |",
        plugins: ["lists", "advlist", "paste", "link", "autoresize", "image", "imagetools", "preview", "code", "table", "pagebreak", "charmap", "directionality", "anchor", "media"],
        fontsize_formats: "10px 12px 14px 16px 18px 24px 36px 48px",
        font_formats: "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats;Open Sans=Open Sans, Helvetica, Tahoma, sans-serif"
      },
      googleMap: null,
      googleMapMarker: null,
      setupStoreState: SetupStore.state
    };
  },

  computed: {
    submitButtonName () {
      return this.merchant.id ? this.$t("common.update") : this.$t("common.create");
    },

    isMapVisible () {
      return +this.merchant.map_setting !== Dashboard.mapSettings.dontShowMap;
    },

    companyLogo () {
      if (this.merchant.logo) {
        return this.merchant.logo.company_logo;
      } else {
        return null;
      }
    },

    companyCover () {
      if (this.merchant.cover) {
        return this.merchant.cover.company_cover_3x;
      } else {
        return null;
      }
    },

    hasAddressInfo () {
      return this.merchant.address || this.merchant.city || this.merchant.country;
    },

    setupComplete () {
      return this.setupStoreState.stage === 4;
    },

    submitButtonText () {
      if (this.setupComplete) {
        return "";
      } else {
        return this.$t("common.next");
      }
    }
  },

  watch: {
    "merchant.map_setting": function () {
      this.showMap();
    }
  },

  mounted () {
    this.initializeMap();
    this.showMap();
    this.initializeCountryCodes();
  },

  methods: {
    onInput ({ number, isValid, country }) {
      this.selectedPhoneCountryCode = country.dialCode;
    },

    initializeMap () {
      this.googleMap = new google.maps.Map(document.getElementById("google-map"), {
        zoom: 18,
        scrollwheel: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      });

      this.googleMapMarker = new google.maps.Marker({
        position: this.googleMap.getCenter(),
        map: this.googleMap,
        draggable: false
      });
    },

    showMap () {
      if (this.isLocateAddress()) {
        this.$nextTick(function () {
          this.locateAddress();
        });
      }

      if (this.isPinOnMap()) {
        this.$nextTick(function () {
          this.pinOnMap();
        });
      }
    },

    initializeCountryCodes () {
      this.selectedPhoneCountryCode = this.$refs.mobileInput.activeCountry.dialCode;
    },

    isLocateAddress () {
      return +this.merchant.map_setting === Dashboard.mapSettings.suggestFromAddress;
    },

    locateAddress () {
      const map = this.googleMap;
      const marker = this.googleMapMarker;
      marker.setDraggable(false);

      if (this.hasAddressInfo) {
        new google.maps.Geocoder().geocode({
          address: this.buildAddress()
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            map.setCenter(results[0].geometry.location);
            map.setZoom(18);
            marker.setPosition(map.getCenter());

            this.merchant.latitude = marker.position.lat();
            this.merchant.longitude = marker.position.lng();
          } else {
            map.setCenter(new google.maps.LatLng(0, 0));
            map.setZoom(5);

            marker.setPosition(map.getCenter());
          }
        }
        );
      } else {
        map.setCenter(new google.maps.LatLng(0, 0));
        map.setZoom(5);

        marker.setPosition(map.getCenter());
      }
    },

    buildAddress () {
      return `${this.merchant.name}, ${this.merchant.address}, ${this.merchant.city}, ${this.merchant.country}`;
    },

    isPinOnMap () {
      return this.merchant.map_setting == Dashboard.mapSettings.pinOnMap;
    },

    pinOnMap () {
      const mapCenter = {
        lat: this.merchant.latitude || 33.8938,
        lng: this.merchant.longitude || 35.5018
      };

      const map = this.googleMap;
      const marker = this.googleMapMarker;

      map.setCenter(mapCenter);
      map.setZoom(18);
      marker.setDraggable(true);
      marker.setPosition(map.getCenter());

      google.maps.event.addListener(marker, "dragend", () => {
        this.merchant.latitude = marker.position.lat();
        this.merchant.longitude = marker.position.lng();
      });
    },

    validateNumber (formattedNumber, { number, valid, country }) {
      if (number.input && !valid) {
        document.getElementById("mobileInput").setCustomValidity("Please enter a valid mobile number.");
      } else {
        document.getElementById("mobileInput").setCustomValidity("");
      }
    }
  }
};
</script>
